import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {Box, Pagination, Skeleton, Stack, TextField, Typography, useMediaQuery} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {useTranslation} from 'react-i18next'
import moment from 'moment'
import DashboardCard from '@containers/dashboard/ui/DashboardCard'
import {useDashboardNewsQuery} from '@containers/dashboard/queryHooks/useDashboardQuery'
import {sanitizeNewsHtml} from '@containers/dashboard/helpers/sanitizeNewsHtml'
import {useDebounce} from '@containers/dashboard/helpers/useDebounce'
import {IDashboardNews} from '@containers/dashboard/types'

// News are few, so all of them are loaded and split into pages by what fits.
const FETCH_LIMIT = 200
const MOBILE_PAGE_SIZE = 3
const GAP = 12

interface IItemProps {
	item: IDashboardNews & {html: string}
}

const NewsItem = ({item}: IItemProps) => (
	<Box sx={{borderLeft: '3px solid', borderColor: item.color || 'primary.main', pl: 1.25}}>
		<Typography variant={'caption'} color={'text.secondary'}>
			{moment(item.publication || item.creation, ['YYYY/MM/DD HH:mm', 'YYYY/MM/DD', moment.ISO_8601]).format('DD.MM.YYYY')}
		</Typography>
		<Typography variant={'body2'} fontWeight={600} sx={{color: item.color || 'text.primary'}}>
			{item.title}
		</Typography>
		<Typography
			variant={'body2'}
			color={'text.secondary'}
			component={'div'}
			sx={{'& p': {m: 0}, overflowWrap: 'anywhere'}}
			dangerouslySetInnerHTML={{__html: item.html}}
		/>
	</Box>
)

// Greedy split: as many items per page as fit into the available height, at least one.
const splitIntoPages = (heights: number[], available: number): number[][] => {
	const pages: number[][] = []
	let current: number[] = []
	let used = 0
	heights.forEach((height, index) => {
		const next = used + (current.length ? GAP : 0) + height
		if (current.length && next > available) {
			pages.push(current)
			current = [index]
			used = height
		} else {
			current.push(index)
			used = next
		}
	})
	if (current.length) pages.push(current)
	return pages
}

const NewsCard = () => {
	const {t} = useTranslation()
	const isWide = useMediaQuery(useTheme().breakpoints.up('md'))
	const [page, setPage] = useState(1)
	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search)

	const {data, isLoading} = useDashboardNewsQuery({start: 0, length: FETCH_LIMIT, search: debouncedSearch})

	const news = useMemo(() => (data?.list ?? []).map((item) => ({
		...item,
		html: sanitizeNewsHtml(item.text),
	})), [data])

	const viewportRef = useRef<HTMLDivElement>(null)
	const measureRef = useRef<HTMLDivElement>(null)
	const [size, setSize] = useState({width: 0, height: 0})
	const [pages, setPages] = useState<number[][]>([])

	useEffect(() => {
		const node = viewportRef.current
		if (!node) return
		const observer = new ResizeObserver(([entry]) => {
			const {width, height} = entry.contentRect
			setSize((prev) => prev.width === width && prev.height === height ? prev : {width, height})
		})
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	useLayoutEffect(() => {
		if (!isWide) {
			setPages(Array.from({length: Math.ceil(news.length / MOBILE_PAGE_SIZE)}, (_, p) =>
				news.slice(p * MOBILE_PAGE_SIZE, (p + 1) * MOBILE_PAGE_SIZE).map((_, i) => p * MOBILE_PAGE_SIZE + i)))
			return
		}
		const heights = Array.from(measureRef.current?.children ?? []).map((child) => (child as HTMLElement).offsetHeight)
		if (heights.length !== news.length || !size.height) return
		setPages(splitIntoPages(heights, size.height))
	}, [news, size, isWide])

	useEffect(() => {
		if (page > Math.max(pages.length, 1)) setPage(Math.max(pages.length, 1))
	}, [pages, page])

	const visible = (pages[page - 1] ?? []).map((index) => news[index]).filter(Boolean)

	return (
		<DashboardCard
			title={t('dashboardPage.news')}
			sx={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: {md: 360}}}
		>
			<TextField
				size={'small'}
				fullWidth
				placeholder={t('dashboardPage.search')}
				value={search}
				onChange={(e) => {
					setSearch(e.target.value)
					setPage(1)
				}}
				sx={{mb: 1.5}}
			/>

			{/* On md+ the list fills the space left by the right column; absolute so it does not grow the row. */}
			<Box ref={viewportRef} sx={{position: 'relative', flex: 1, minHeight: {xs: 0, md: 120}}}>
				<Stack gap={`${GAP}px`} sx={{position: {md: 'absolute'}, inset: 0, overflow: {md: 'hidden'}}}>
					{isLoading && Array.from({length: MOBILE_PAGE_SIZE}, (_, index) => (
						<Box key={index} sx={{borderLeft: '3px solid', borderColor: 'divider', pl: 1.25}}>
							<Skeleton width={70} height={16}/>
							<Skeleton width={'50%'}/>
							<Skeleton width={'90%'}/>
						</Box>
					))}
					{visible.map((item, index) => <NewsItem key={`${page}-${index}`} item={item}/>)}
					{data && !news.length && <Typography variant={'body2'} color={'text.secondary'}>{t('dashboardPage.empty')}</Typography>}
				</Stack>

				{/* Invisible copy at the same width, used only to measure item heights. */}
				{isWide && <Box
					ref={measureRef}
					aria-hidden
					sx={{position: 'absolute', top: 0, left: 0, right: 0, visibility: 'hidden', pointerEvents: 'none'}}
				>
					{news.map((item, index) => <NewsItem key={index} item={item}/>)}
				</Box>}
			</Box>

			{pages.length > 1 && <Pagination
				size={'small'}
				count={pages.length}
				page={page}
				onChange={(_, value) => setPage(value)}
				sx={{mt: 1.5}}
			/>}
		</DashboardCard>
	)
}

export default NewsCard
